// Device Forecast — static seed data for the pipeline forecast view.
// All hex values here are chart-only constants that Chart.js requires as literal
// strings; they mirror the values defined in owl-bench-tokens.css.

// ── Chart.js dataset colour constants (match token file values) ──────────────
export const CHART_SUCCESS = '#4ade80';   // --success-text
export const CHART_WARNING = '#fbbf24';   // --warning-text
export const CHART_DANGER  = '#f87171';   // --danger-text
export const CHART_BRAND   = '#ff7a45';   // --brand-light
export const CHART_TRACK   = '#374151';   // neutral grey for raw bars

// Sector doughnut colours — one per sector, in sector order
export const SECTOR_COLORS = ['#60a5fa', '#ff7a45', '#4ade80', '#fbbf24', '#c084fc'];

// ── Calendar constants ────────────────────────────────────────────────────────
export const FORECAST_YEAR    = 2026;
export const FORECAST_MONTHS  = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// Current month index within the 2026 window (0 = Jan … 11 = Dec).
// Falls back to 3 (April) when running outside the forecast year.
const _now = new Date();
export const FORECAST_NOW_IDX = _now.getFullYear() === FORECAST_YEAR ? _now.getMonth() : 3;

// ── Pre-calculated monthly aggregates ────────────────────────────────────────
// Raw demand total across all projects, per month
export const RAW_MONTHLY      = [25, 50, 60, 180, 300, 705, 1075, 1270, 1490, 1690, 1970, 2170];
// Probability-weighted demand, per month
export const WEIGHTED_MONTHLY = [23, 45, 53, 144, 242, 465,  702,  871, 1032, 1189, 1362, 1497];
// Month-on-month raw growth (null for January — no prior month)
export const GROWTH_NUMBERS   = [null, 22, 8, 91, 98, 223, 237, 169, 161, 157, 173, 135];

// ── Forecast status lifecycle ─────────────────────────────────────────────────
// 'pipeline'  — interest confirmed but client not yet committed
// 'confirmed' — client has formally committed; ready to create a location
// 'converted' — location has been created from this forecast entry
export const FORECAST_STATUSES = ['pipeline', 'confirmed', 'converted'];

// ── Sector + project seed data ────────────────────────────────────────────────
export const FORECAST_SECTORS = [
  {
    name: 'UK Rail',
    projects: [
      {
        id: 'fc-001',
        name: 'NR Sussex Broader Roll-out',
        client: 'Network Rail',
        deviceType: 'RH1',
        total: 300,
        likelihood: 0.90,
        status: 'pipeline',
        notes: null,
        locationId: null,
        monthly: [null, null, null, null, 50, 100, 150, 200, 200, 200, 200, 200],
      },
      {
        id: 'fc-002',
        name: 'NR Kent',
        client: 'Network Rail',
        deviceType: 'RH1',
        total: 300,
        likelihood: 0.75,
        status: 'pipeline',
        notes: null,
        locationId: null,
        monthly: [null, null, null, null, null, 25, 50, 50, 100, 100, 200, 200],
      },
      {
        id: 'fc-003',
        name: 'NR Wessex',
        client: 'Network Rail',
        deviceType: 'RH1',
        total: 300,
        likelihood: 0.75,
        status: 'pipeline',
        notes: null,
        locationId: null,
        monthly: [null, null, null, null, null, null, 25, 50, 50, 100, 100, 200],
      },
      {
        id: 'fc-004',
        name: 'NR Sussex ALO - Pilot',
        client: 'Network Rail',
        deviceType: 'RH1',
        total: 20,
        likelihood: 1.00,
        status: 'pipeline',
        notes: null,
        locationId: null,
        monthly: [20, 20, 20, 20, null, null, null, null, null, null, null, null],
      },
      {
        id: 'fc-005',
        name: 'M Group Rail',
        client: 'M Group',
        deviceType: 'RH1',
        total: 240,
        likelihood: 0.90,
        status: 'pipeline',
        notes: null,
        locationId: null,
        monthly: [null, null, null, 40, 60, 80, 120, 160, 200, 250, 300, 300],
      },
      {
        id: 'fc-006',
        name: 'SRE Framework',
        client: 'Network Rail',
        deviceType: 'RH1',
        total: 400,
        likelihood: 0.90,
        status: 'pipeline',
        notes: null,
        locationId: null,
        monthly: [null, null, null, null, 50, 50, 100, 150, 200, 250, 300, 350],
      },
      {
        id: 'fc-007',
        name: 'CRSA Possession',
        client: 'Network Rail',
        deviceType: 'RH1',
        total: 80,
        likelihood: 0.50,
        status: 'pipeline',
        notes: null,
        locationId: null,
        monthly: [null, null, null, null, null, null, null, null, null, null, null, null],
      },
      {
        id: 'fc-008',
        name: 'Kier Rail (and EWR)',
        client: 'Kier Rail',
        deviceType: 'RH1',
        total: null,
        likelihood: 0.50,
        status: 'pipeline',
        notes: null,
        locationId: null,
        monthly: [null, null, null, null, 20, 20, 20, 20, null, null, null, null],
      },
      {
        id: 'fc-009',
        name: 'Story Contracting',
        client: 'Story',
        deviceType: 'RH1',
        total: 50,
        likelihood: 0.20,
        status: 'pipeline',
        notes: null,
        locationId: null,
        monthly: [null, null, null, null, null, null, null, null, null, null, null, null],
      },
      {
        id: 'fc-010',
        name: 'TfL',
        client: 'TfL',
        deviceType: 'RH1',
        total: 40,
        likelihood: 0.10,
        status: 'pipeline',
        notes: null,
        locationId: null,
        monthly: [null, null, null, null, null, null, null, null, null, null, null, null],
      },
    ],
  },
  {
    name: 'Aviation',
    projects: [
      {
        id: 'fc-011',
        name: 'M Group MAG Airports',
        client: 'M Group / MAG',
        deviceType: 'RH2',
        total: 30,
        likelihood: 1.00,
        status: 'pipeline',
        notes: null,
        locationId: null,
        monthly: [null, 20, 20, 20, 20, 20, 20, 20, 20, 20, 20, 20],
      },
      {
        id: 'fc-012',
        name: 'Heathrow (direct)',
        client: 'Heathrow',
        deviceType: 'RH2',
        total: 1000,
        likelihood: 0.70,
        status: 'pipeline',
        notes: null,
        locationId: null,
        monthly: [null, null, null, null, null, null, null, null, null, null, null, null],
      },
      {
        id: 'fc-013',
        name: 'Volker Heathrow',
        client: 'Volker',
        deviceType: 'RH2',
        total: 30,
        likelihood: 0.70,
        status: 'pipeline',
        notes: null,
        locationId: null,
        monthly: [null, null, null, 30, 30, 30, 30, 30, 30, 30, 30, 30],
      },
      {
        id: 'fc-014',
        name: 'Gatwick Airport (via PJ Hagerty)',
        client: 'Gatwick',
        deviceType: 'RH2',
        total: 100,
        likelihood: 0.60,
        status: 'pipeline',
        notes: null,
        locationId: null,
        monthly: [null, null, null, 30, 30, 30, 50, 50, 50, 100, 100, 100],
      },
    ],
  },
  {
    name: 'Transport',
    projects: [
      {
        id: 'fc-015',
        name: 'First Bus',
        client: 'First Bus',
        deviceType: 'Teltonika',
        total: 200,
        likelihood: 0.50,
        status: 'pipeline',
        notes: null,
        locationId: null,
        monthly: [5, 10, 10, 10, 10, 10, 50, 50, 50, 50, 50, 50],
      },
      {
        id: 'fc-016',
        name: 'Go Ahead',
        client: 'Go Ahead',
        deviceType: 'Teltonika',
        total: 300,
        likelihood: 0.40,
        status: 'pipeline',
        notes: null,
        locationId: null,
        monthly: [null, null, null, null, null, null, 100, 100, 100, 100, 100, 100],
      },
    ],
  },
  {
    name: 'General Construction',
    projects: [
      {
        id: 'fc-017',
        name: 'BAM Pilot with EA',
        client: 'BAM / EA',
        deviceType: 'RH1',
        total: 50,
        likelihood: 0.80,
        status: 'pipeline',
        notes: null,
        locationId: null,
        monthly: [null, null, null, 20, 20, 20, 20, 20, 20, 20, 20, 20],
      },
      {
        id: 'fc-018',
        name: 'Anglian Water',
        client: 'Anglian Water',
        deviceType: 'RH1',
        total: 30,
        likelihood: 0.50,
        status: 'pipeline',
        notes: null,
        locationId: null,
        monthly: [null, null, null, null, null, null, null, null, null, null, null, null],
      },
      {
        id: 'fc-019',
        name: 'Vinci / Taylor Woodrow',
        client: 'Vinci',
        deviceType: 'RH1',
        total: 20,
        likelihood: 0.10,
        status: 'pipeline',
        notes: null,
        locationId: null,
        monthly: [null, null, null, null, null, null, null, null, null, null, null, null],
      },
    ],
  },
  {
    name: 'International',
    projects: [
      {
        id: 'fc-020',
        name: 'Scandinavian Reseller',
        client: 'Reseller',
        deviceType: 'RH1',
        total: 50,
        likelihood: 0.80,
        status: 'pipeline',
        notes: null,
        locationId: null,
        monthly: [null, null, null, null, null, 20, 20, 50, 50, 50, 50, 50],
      },
      {
        id: 'fc-021',
        name: 'TTC',
        client: 'TTC',
        deviceType: 'RH1',
        total: 100,
        likelihood: 0.10,
        status: 'pipeline',
        notes: null,
        locationId: null,
        monthly: [null, null, null, null, null, null, 20, 20, 20, 20, 100, 100],
      },
      {
        id: 'fc-022',
        name: 'ProRail',
        client: 'ProRail',
        deviceType: 'RH1',
        total: 300,
        likelihood: 0.75,
        status: 'pipeline',
        notes: null,
        locationId: null,
        monthly: [null, null, 10, 10, 10, 50, 50, 50, 100, 100, 100, 100],
      },
      {
        id: 'fc-023',
        name: 'SNCF',
        client: 'SNCF',
        deviceType: 'RH1',
        total: 600,
        likelihood: 0.40,
        status: 'pipeline',
        notes: null,
        locationId: null,
        monthly: [null, null, null, null, null, 200, 200, 200, 200, 200, 200, 200],
      },
      {
        id: 'fc-024',
        name: 'Infrabel',
        client: 'Infrabel',
        deviceType: 'RH1',
        total: 300,
        likelihood: 0.30,
        status: 'pipeline',
        notes: null,
        locationId: null,
        monthly: [null, null, null, null, null, 50, 50, 50, 100, 100, 100, 150],
      },
    ],
  },
];
